import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing admin session on mount
  useEffect(() => {
    try {
      const adminSession = localStorage.getItem('adminSession');
      const sessionExpiry = localStorage.getItem('adminSessionExpiry');
      
      if (adminSession && sessionExpiry) {
        const now = new Date().getTime();
        const expiry = parseInt(sessionExpiry);
        
        if (now < expiry) {
          setIsAdminAuthenticated(true);
        } else {
          // Session expired, clear it
          localStorage.removeItem('adminSession');
          localStorage.removeItem('adminSessionExpiry');
        }
      }
    } catch (error) {
      console.warn('Error checking admin session:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const adminLogin = (email, password) => {
    const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
    const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;
    
    if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
      const sessionToken = 'admin_' + Date.now();
      const expiryTime = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
      
      localStorage.setItem('adminSession', sessionToken);
      localStorage.setItem('adminSessionExpiry', expiryTime.toString());
      setIsAdminAuthenticated(true);
      
      return { success: true };
    } else {
      return { success: false, error: 'Invalid email or password' };
    }
  };

  const adminLogout = () => {
    localStorage.removeItem('adminSession');
    localStorage.removeItem('adminSessionExpiry');
    setIsAdminAuthenticated(false);
  };

  const value = {
    isAdminAuthenticated,
    isLoading,
    adminLogin,
    adminLogout
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
